import { useEffect, useMemo, useState } from 'react';
import { listSuppliers } from '../services/supplier.service';
import { getAllWasteCatalogMaterials, type WasteCatalogOption } from '../services/waste-catalog.service';
import { useCollectionPointStore } from '../stores/collection-point.store';
import type { Supplier } from '../types/supplier.type';

export interface CollectionPointsMapFilterState {
  search: string;
  supplierId: string;
  materialId: string;
}

const EMPTY_FILTERS: CollectionPointsMapFilterState = { search: '', supplierId: '', materialId: '' };

/** Shared data-fetching for any "org-wide collection points on a map" view (Suppliers list map tab, Collection Points map tab). */
export function useCollectionPointsMapData(organizationId: string | undefined, enabled: boolean) {
  const { collectionPoints, isLoading, fetchCollectionPoints } = useCollectionPointStore();
  const [filters, setFiltersState] = useState<CollectionPointsMapFilterState>(EMPTY_FILTERS);
  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [materialOptions, setMaterialOptions] = useState<WasteCatalogOption[]>([]);

  useEffect(() => {
    if (!organizationId) return;
    void listSuppliers({ organization_id: organizationId, limit: 100 }).then((result) => setSupplierOptions(result.suppliers)).catch(() => setSupplierOptions([]));
    // Unscoped on purpose: these are labels for existing records, which may
    // reference a waste type this organization no longer handles.
    void getAllWasteCatalogMaterials().then(setMaterialOptions).catch(() => setMaterialOptions([]));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId || !enabled) return;
    void fetchCollectionPoints({
      organization_id: organizationId,
      page: 1,
      limit: 500,
      search: filters.search.trim() || undefined,
      supplier_id: filters.supplierId || undefined,
      material_id: filters.materialId || undefined,
    });
  }, [organizationId, enabled, filters, fetchCollectionPoints]);

  const supplierNameById = useMemo(() => new Map(supplierOptions.map((supplier) => [supplier.id, supplier.name])), [supplierOptions]);
  const materialLabels = useMemo(() => new Map(materialOptions.map((option) => [option.value, option.label])), [materialOptions]);

  const setFilters = (next: Partial<CollectionPointsMapFilterState>) => setFiltersState((prev) => ({ ...prev, ...next }));

  return {
    collectionPoints,
    isLoading,
    filters,
    setFilters,
    supplierOptions: supplierOptions.map((supplier) => ({ id: supplier.id, name: supplier.name })),
    supplierNameById,
    materialLabels,
  };
}
